import React, { useEffect, useLayoutEffect, useState } from 'react';
import { useMediaQuery, useTheme } from '@material-ui/core';
import BottomButtonGroup from './BottomButtonGroup';
import PurchaseOrderViewDropdown from './PurchaseOrderViewDropdown';
import PurchaseOrderViewListDrawer from './PurchaseOrderViewListDrawer';
import {
  PurchaseOrderFixedBottomCTA,
  PurchaseOrderStepper,
  PurchaseOrderHeader,
  PurchaseOrderNotFound
} from '../Common';
import useDocuments from '../../../../hooks/useDocuments';
import { snackbarBottom } from '@vars';
import PurchaseOrderViewPresenter from './PurchaseOrderViewPresenter';
import usePurchaseOrder from './hooks/usePurchaseOrder';
import { useHistory } from 'react-router-dom';
import { CapaQuotationRfqPurchaseOrder } from '@generated/graphql';
import PurchaseOrderHistoryModal from './PurchaseOrderHistoryModal';

interface PurchaseOrderViewProps {
  rfqId?: string;
  partnerId?: string;
  chatRoomId?: string;
  hasQuotations?: boolean;
  hasPurchaseOrder?: boolean;
  isHistory?: boolean;
}

const PurchaseOrderView: React.FC<PurchaseOrderViewProps> = ({
  rfqId,
  partnerId,
  chatRoomId,
  hasQuotations = false,
  hasPurchaseOrder = false,
  isHistory = false
}) => {
  const {
    shipping,
    projectName,
    serviceType,
    purchaseOrders,
    paymentDataList,
    currentStepIndex,
    isClosedConsulting,
    selectedPurchaseOrder,
    isStoppedPurchaseOrder,
    selectedPurchaseOrderId
  } = usePurchaseOrder({ rfqId, quotationPartnerId: partnerId });
  const purchaseOrdersHistory = purchaseOrders.slice(1);
  const hasPurchaseOrderHistory = purchaseOrdersHistory.length > 0;
  const history = useHistory();
  const { onInitDocuments } = useDocuments();
  const matches = useMediaQuery(useTheme().breakpoints.down('md'));
  const isDashboard = window.location.pathname.includes('/dashboard');
  const hasPaymentPlanInfo = Boolean(selectedPurchaseOrder?.payments?.[0]);
  const hasInspectionInfo = Boolean(selectedPurchaseOrder?.inspectionPeriod);

  const [
    purchaseOrderHistory,
    setPurchaseOrderHistory
  ] = useState<CapaQuotationRfqPurchaseOrder | null>(null);

  useEffect(() => {
    snackbarBottom(88);

    return () => {
      snackbarBottom(0);
    };
  }, [matches]);

  useEffect(() => {
    // 스크롤 상단 이동
    const container = document.getElementById('drawer-content-purchaseorder');

    container?.scrollTo({ top: 0 });
  }, []);

  useEffect(
    // Clean up
    () => () => {
      onInitDocuments();
    },
    [onInitDocuments]
  );

  const onClick = (id: string) => {
    const purchaseOrder = id
      ? purchaseOrders.find((order) => order.id === id)
      : purchaseOrders?.[0];

    if (purchaseOrder) {
      setPurchaseOrderHistory(purchaseOrder);
    }
  };

  useLayoutEffect(() => {
    if (!hasPurchaseOrder && hasQuotations) {
      history.push(
        history.location.pathname + '?menu=create_purchaseorder&p=2'
      );
    }
  }, [hasPurchaseOrder, hasQuotations, history]);

  if (!selectedPurchaseOrder) {
    return (
      <PurchaseOrderNotFound
        rfqId={rfqId}
        partnerId={partnerId}
        isClosedConsulting={isClosedConsulting}
      />
    );
  }

  return (
    <>
      <PurchaseOrderHistoryModal
        open={Boolean(purchaseOrderHistory)}
        onClose={() => setPurchaseOrderHistory(null)}
        matches={matches}
        selectedPurchaseOrder={selectedPurchaseOrder}
        projectName={projectName}
        payment={paymentDataList?.[0]}
        shipping={shipping}
        hasPaymentPlanInfo={hasPaymentPlanInfo}
        hasInspectionInfo={hasInspectionInfo}
        serviceType={serviceType}
      />

      {matches && hasPurchaseOrderHistory && (
        <PurchaseOrderViewListDrawer
          purchaseOrders={purchaseOrders}
          onClick={onClick}
        />
      )}
      {!matches && hasPurchaseOrderHistory && (
        <PurchaseOrderViewDropdown
          purchaseOrders={purchaseOrders}
          onClick={onClick}
        />
      )}

      {hasPurchaseOrder ? (
        <>
          {!isDashboard && (
            <PurchaseOrderHeader
              hasPurchaseOrderHistory={hasPurchaseOrderHistory}
            />
          )}

          {!isDashboard && !selectedPurchaseOrderId && (
            <PurchaseOrderStepper
              activeStep={currentStepIndex}
              isStopped={isStoppedPurchaseOrder}
            />
          )}

          <PurchaseOrderViewPresenter
            matches={matches}
            selectedPurchaseOrder={selectedPurchaseOrder}
            projectName={projectName}
            payment={paymentDataList?.[0]}
            shipping={shipping}
            hasPaymentPlanInfo={hasPaymentPlanInfo}
            hasInspectionInfo={hasInspectionInfo}
            serviceType={serviceType}
            isHistory={isHistory}
          />

          {!isDashboard && !selectedPurchaseOrderId && (
            <PurchaseOrderFixedBottomCTA>
              <BottomButtonGroup
                rfqId={rfqId}
                isClosedConsulting={isClosedConsulting}
                quotationPartnerId={partnerId}
                chatRoomId={chatRoomId}
              />
            </PurchaseOrderFixedBottomCTA>
          )}
        </>
      ) : (
        <PurchaseOrderNotFound
          rfqId={rfqId}
          partnerId={partnerId}
          isClosedConsulting={isClosedConsulting}
        />
      )}
    </>
  );
};

export default PurchaseOrderView;
